import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Select, Row, Col } from "antd";
import {
  createMasterData,
  updateMasterData,
} from "../../../../Services/MasterDataService";
import { masterDataCreationSchema } from "../../../../Schemas/MasterDataCreationSchema";
import * as yup from "yup";

const { Option } = Select;

const MasterDataCreationForm = ({
  initialValues,
  articleData,
  callReFetchMasterData,
  colorData,
  isEditMode,
  data,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState("");

  useEffect(() => {
    form.resetFields();
    if (isEditMode && data) {
      form.setFieldsValue({
        article: data.article,
        size: data.size,
        color: data.color,
        barcode: data.barcode,
      });
    } else if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [isEditMode, data, initialValues, form]);

  const handleFormValuesChange = (changedValues, allValues) => {
    const { article, color, size } = allValues;
    if (
      ("article" in changedValues ||
        "color" in changedValues ||
        "size" in changedValues) &&
      article &&
      color &&
      size &&
      articleData?.length > 0 &&
      colorData?.length > 0
    ) {
      const selectedArticle = articleData.find((a) => a.name === article);
      const selectedColor = colorData.find((c) => c.name === color);
      const newBarcode = `${selectedArticle?.preBarcode || ""}${
        selectedColor?.code || ""
      }${size || ""}`;
      setBarcode(newBarcode);
      form.setFieldsValue({ barcode: newBarcode });
    }
  };

  const handleSubmit = async (values) => {
    try {
      await masterDataCreationSchema.validate(values);
      setLoading(true);

      if (isEditMode || (initialValues && initialValues._id)) {
        await updateMasterData(data._id, values);
        message.success("Master data updated successfully");
        if (onClose) onClose();
      } else {
        const size = values.size.toString();
        if (size.length === 4) {
          const startSize = parseInt(size.substring(0, 2));
          const endSize = parseInt(size.substring(2, 4));

          for (let i = startSize; i <= endSize; i++) {
            const newValues = {
              ...values,
              size: i.toString().padStart(2, "0"),
            };
            await createMasterData(newValues);
          }
          message.success(
            `Created master data entries for sizes ${startSize} to ${endSize}`
          );
        } else {
          await createMasterData(values);
          message.success("Master data created successfully");
        }
      }
      callReFetchMasterData();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        message.error(error.message);
      } else {
        console.error(error);
        message.error("Failed to save master data");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={handleFormValuesChange}
    >
      <Form.Item
        name="article"
        label="Article"
        rules={[{ required: true, message: "Please select an article!" }]}
      >
        <Select
          showSearch
          placeholder="Select Article"
          optionFilterProp="children"
          disabled={isEditMode}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {articleData.map((article) => (
            <Option key={article.name} value={article.name}>
              {article.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="size"
            label="Size"
            rules={[{ required: true, message: "Please input size!" }]}
          >
            <Input placeholder="Input Size" disabled={isEditMode} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="color" label="Color">
            <Select
              showSearch
              placeholder="Select Color"
              optionFilterProp="children"
              disabled={isEditMode}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {colorData.map((color) => (
                <Option key={color.name} value={color.name}>
                  {color.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="barcode" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {isEditMode ? "Update" : "Create"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default MasterDataCreationForm;
